<script>
import proofToggles from "../../components/global/proof-toggle";
import reminderFrequency from "../../components/form/reminderFrequency";
import { TYPES_ENUM } from "../../utils/constants";
import { isWatermarkPlanEnabled } from "../../utils/helpers";

const { FUNCTION } = TYPES_ENUM;

const PROOF_SETTING_TOGGLES = [
  {
    key: "downloadFiles",
    id: "proof_toggle_download",
    dataCy: "downloadCheckbox",
    title: "Download Files",
    description: "Allow approvers to download the files in your proof.",
  },
  {
    key: "approveIndividualFiles",
    id: "proof_toggle_approve",
    dataCy: null,
    title: "Approve Individual Files",
    description: "Require approvers to make an approval decision on each file in your proof. Otherwise, we'll ask for one approval decision for all files in this proof.",
  },
  {
    key: "viewAllComments",
    id: "proof_toggle_comments",
    dataCy: null,
    title: "View All Comments",
    description: "Allow your approvers to see and interact with other approver comments on a proof.",
  },
  {
    key: "approveWithChanges",
    id: "proof_toggle_changes",
    dataCy: null,
    title: "Approve With Changes",
    description: 'Allow your approvers to say, "make these changes but I don\'t need to see a new version". We\'ll treat the proof as approved.',
  },
  {
    key: "limitVersionVisibility",
    id: "proof_toggle_limitVersionVisibility",
    dataCy: null,
    title: "Limit Version Visibility",
    description: "Prevent approvers from viewing proof versions sent to approvers in other workflow stages.",
  },
  {
    key: "blockCommentingAfterProofDecision",
    id: "proof_toggle_blockCommentingAfterProofDecision",
    dataCy: "blockCommentingCheckbox",
    title: "Block Commenting After Proof Decision",
    description: "Prevent approvers from submitting comments after making a decision.",
  },
];

// Watermarking is only available on standard and premium plans.
const WATERMARK_SETTING_TOGGLE = {
  key: "watermarkEnabled",
  id: "proof_toggle_watermarkEnabled",
  dataCy: null,
  title: "Watermark Files",
  description: "Stamp your account logo on every page of PDFs uploaded to your proofs.",
};

export default {
  name: "profilePreferences",
  components: {
    proofToggles,
    reminderFrequency,
  },
  mounted: function () {
    const setData = _.get(this, '$refs.reminderFreq.setData');
    if (!setData && typeof setData !== FUNCTION) return;

    setData(
        this.userSettings.reminderTimeOfDay,
        this.userSettings.reminderDays,
        this.userSettings.reminderTimeZone
    );
  },
  data: function () {
    return {
      user: this.$A.Auth,
      proofSettingToggles: [
        ...PROOF_SETTING_TOGGLES,
        ...(isWatermarkPlanEnabled(this.$A.Auth)
            ? [WATERMARK_SETTING_TOGGLE]
            : []),
      ],
    };
  },
  computed: {
    userSettings : function () {
      return _.get(this, 'user.userSettings', {});
    }
  },
  methods: {
    update: async function (shouldUpdateRemainderFrequency) {
      let reminderDays = this.$refs.reminderFreq.frequency;
      let reminderTimeOfDay = this.$refs.reminderFreq.time_of_day_to_send;
      let reminderTimeZone = this.$refs.reminderFreq.timeZone;

      if (!shouldUpdateRemainderFrequency) {
        reminderDays = this.userSettings.reminderDays;
        reminderTimeOfDay = this.userSettings.reminderTimeOfDay;
        reminderTimeZone = this.userSettings.reminderTimeZone;
      }

      let user = await this.$A.UserService.UpdateUserDefaultSettings(
          this.$A.Auth.id,
          this.userSettings.downloadFiles,
          this.userSettings.approveIndividualFiles,
          this.userSettings.viewAllComments,
          this.userSettings.approveWithChanges,
          this.userSettings.notifyFilesSent,
          this.userSettings.notifyFilesApproved,
          this.userSettings.notifyNewComments,
          this.userSettings.notifyMeNewComments,
          this.userSettings.notifyMyApproversNewComments,
          this.userSettings.limitVersionVisibility,
          this.userSettings.blockCommentingAfterProofDecision,
          reminderDays,
          reminderTimeOfDay,
          reminderTimeZone,
          this.userSettings.watermarkEnabled,
          this.userSettings.dailyDigestTimeOfDay,
          this.userSettings.notifyJobAssigned
      );
      if (user.id !== undefined && user.id > 0) {
        this.user = user;
        this.$A.UpdateUserObjectAndBustCache(user);
      }
    },
  },
};
</script>

<template>
  <div class="preferences-stack">
    <!-- Automatic reminders -->
    <div class="settings-card">
      <div class="card-head">
        <div class="card-head-title">Automatic Reminders</div>
        <div class="card-head-subtitle">The default reminder schedule for approvers on your proofs.</div>
      </div>
      <div class="card-body reminder-body">
        <reminderFrequency ref="reminderFreq"/>
      </div>
      <div class="card-foot">
        <a
            class="primary-btn"
            data-cy="updatedProfileDefault"
            @click="update(true)"
        >Update</a>
      </div>
    </div>

    <!-- Default proof settings -->
    <div class="settings-card">
      <div class="card-head">
        <div class="card-head-title">Default New Proof Settings</div>
        <div class="card-head-subtitle">Every new proof you create starts with these permissions. You can still adjust them per proof.</div>
      </div>
      <div class="toggle-rows">
        <div
            v-for="toggle in proofSettingToggles"
            :key="toggle.key"
            class="toggle-row"
            :id="toggle.id"
        >
          <div class="ashore-toggle toggle-control">
            <label class="switch">
              <input
                  type="checkbox"
                  :data-cy="toggle.dataCy"
                  v-model="userSettings[toggle.key]"
              />
              <div class="slider round"></div>
            </label>
          </div>
          <div class="toggle-text">
            <div class="toggle-title">{{ toggle.title }}</div>
            <div class="toggle-description">{{ toggle.description }}</div>
          </div>
        </div>
      </div>
      <div class="card-foot">
        <a
            class="primary-btn"
            data-cy="updatedProfileDefault"
            @click="update"
        >Update</a>
      </div>
    </div>
  </div>
</template>

<style scoped>
.preferences-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.settings-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
}
.card-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.card-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.card-body {
  padding: 20px;
}
.card-foot {
  display: flex;
  justify-content: flex-end;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 0 0 12px 12px;
}

/* Light polish on the shared reminderFrequency component without touching it */
.reminder-body ::v-deep .label-text {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.reminder-body ::v-deep .contact-input {
  border: 1px solid #d1d5db;
  font-size: 13px;
  color: #1f2937;
}
.reminder-body ::v-deep .contact-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}

/* Toggle rows */
.toggle-rows {
  display: flex;
  flex-direction: column;
}
.toggle-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 15px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.toggle-row:last-child {
  border-bottom: 0;
}
.toggle-control {
  flex-shrink: 0;
  margin-top: 1px;
}
.toggle-control .switch {
  margin-right: 0;
}
.toggle-text {
  flex: 1;
  min-width: 0;
}
.toggle-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #111827;
}
.toggle-description {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.5;
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
@media (max-width: 575px) {
  .primary-btn {
    width: 100%;
  }
}
</style>
